/**
 * useInstitutions Hooks
 *
 * React hooks for fetching institution and rating data from BigQuery.
 * Follows the project's existing data fetching patterns with useState/useEffect.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getRatedInstitutions,
  getRatedInstitutionsWithRatings,
  getLatestRating,
  getRatingHistory,
  getIndicatorScores,
  getCacheStats,
  clearCache,
} from '../services/institutionService';
import type {
  RatedInstitution,
  RatedInstitutionWithRating,
  InstitutionRating,
  RatingHistoryEntry,
  IndicatorScore,
  ProvinceStats,
} from '../types/institution';
import { aggregateByProvince } from '../types/institution';

// ============================================================================
// INSTITUTION LIST HOOKS
// ============================================================================

interface UseRatedInstitutionsState {
  institutions: RatedInstitution[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch all rated institutions (metadata only)
 */
export function useRatedInstitutions() {
  const [state, setState] = useState<UseRatedInstitutionsState>({
    institutions: [],
    isLoading: true,
    error: null,
  });

  const fetchInstitutions = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const institutions = await getRatedInstitutions();
      setState({ institutions, isLoading: false, error: null });
    } catch (err) {
      console.error('[useRatedInstitutions] Error:', err);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch institutions',
      }));
    }
  }, []);

  useEffect(() => {
    fetchInstitutions();
  }, [fetchInstitutions]);

  return {
    ...state,
    refetch: fetchInstitutions,
  };
}

interface UseRatedInstitutionsWithRatingsState {
  institutions: RatedInstitutionWithRating[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch all rated institutions with their latest ratings
 * Ideal for map/list views where you need rating data upfront.
 * @param periods - Optional array of periods in YYYY-MM format (e.g., ['2025-05', '2025-04'])
 *                  If not provided, returns the latest ratings.
 */
export function useRatedInstitutionsWithRatings(periods?: string[]) {
  const [state, setState] = useState<UseRatedInstitutionsWithRatingsState>({
    institutions: [],
    isLoading: true,
    error: null,
  });

  // Serialize periods for dependency array comparison
  const periodsKey = periods?.sort().join(',') || '';

  const fetchInstitutions = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const institutions = await getRatedInstitutionsWithRatings(periods);
      setState({ institutions, isLoading: false, error: null });
    } catch (err) {
      console.error('[useRatedInstitutionsWithRatings] Error:', err);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch institutions',
      }));
    }
  }, [periodsKey]);

  useEffect(() => {
    fetchInstitutions();
  }, [fetchInstitutions]);

  // Derived data
  const provinces = useMemo(() => {
    const set = new Set(state.institutions.map((i) => i.province));
    return Array.from(set).sort();
  }, [state.institutions]);

  const segments = useMemo(() => {
    const map = new Map<string, string>();
    for (const inst of state.institutions) {
      if (!map.has(inst.segment_id)) {
        map.set(inst.segment_id, inst.segment_name);
      }
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [state.institutions]);

  const provinceStats = useMemo((): Map<string, ProvinceStats> => {
    return aggregateByProvince(state.institutions);
  }, [state.institutions]);

  return {
    ...state,
    provinces,
    segments,
    provinceStats,
    refetch: fetchInstitutions,
  };
}

// ============================================================================
// SINGLE INSTITUTION HOOKS
// ============================================================================

interface UseLatestRatingState {
  rating: InstitutionRating | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch the latest rating for a specific institution
 */
export function useLatestRating(institutionId: string | null) {
  const [state, setState] = useState<UseLatestRatingState>({
    rating: null,
    isLoading: false,
    error: null,
  });

  const fetchRating = useCallback(async () => {
    if (!institutionId) {
      setState({ rating: null, isLoading: false, error: null });
      return;
    }

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const rating = await getLatestRating(institutionId);
      setState({ rating, isLoading: false, error: null });
    } catch (err) {
      console.error('[useLatestRating] Error:', err);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch rating',
      }));
    }
  }, [institutionId]);

  useEffect(() => {
    fetchRating();
  }, [fetchRating]);

  return {
    ...state,
    refetch: fetchRating,
  };
}

interface UseRatingHistoryState {
  ratings: RatingHistoryEntry[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch rating history for a specific institution
 */
export function useRatingHistory(institutionId: string | null) {
  const [state, setState] = useState<UseRatingHistoryState>({
    ratings: [],
    isLoading: false,
    error: null,
  });

  const fetchHistory = useCallback(async () => {
    if (!institutionId) {
      setState({ ratings: [], isLoading: false, error: null });
      return;
    }

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const ratings = await getRatingHistory(institutionId);
      setState({ ratings, isLoading: false, error: null });
    } catch (err) {
      console.error('[useRatingHistory] Error:', err);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch history',
      }));
    }
  }, [institutionId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    ...state,
    refetch: fetchHistory,
  };
}

interface UseIndicatorScoresState {
  indicators: IndicatorScore[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch indicator scores for a specific institution and period
 */
export function useIndicatorScores(institutionId: string | null, period: string | null) {
  const [state, setState] = useState<UseIndicatorScoresState>({
    indicators: [],
    isLoading: false,
    error: null,
  });

  const fetchIndicators = useCallback(async () => {
    if (!institutionId || !period) {
      setState({ indicators: [], isLoading: false, error: null });
      return;
    }

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const indicators = await getIndicatorScores(institutionId, period);
      setState({ indicators, isLoading: false, error: null });
    } catch (err) {
      console.error('[useIndicatorScores] Error:', err);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch indicators',
      }));
    }
  }, [institutionId, period]);

  useEffect(() => {
    fetchIndicators();
  }, [fetchIndicators]);

  return {
    ...state,
    refetch: fetchIndicators,
  };
}

// ============================================================================
// ADMIN HOOKS
// ============================================================================

interface UseCacheStatsState {
  stats: {
    institutions: number;
    ratings: number;
    indicators: number;
    ttlHours: number;
  } | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to get cache statistics
 */
export function useCacheStats() {
  const [state, setState] = useState<UseCacheStatsState>({
    stats: null,
    isLoading: true,
    error: null,
  });

  const fetchStats = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const stats = await getCacheStats();
      setState({ stats, isLoading: false, error: null });
    } catch (err) {
      console.error('[useCacheStats] Error:', err);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch stats',
      }));
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    ...state,
    refetch: fetchStats,
  };
}

/**
 * Hook to clear the cache (returns a function to trigger)
 */
export function useClearCache() {
  const [isClearing, setIsClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clear = useCallback(async () => {
    try {
      setIsClearing(true);
      setError(null);
      const result = await clearCache();
      console.log('[useClearCache] Cache cleared:', result);
      return result;
    } catch (err) {
      console.error('[useClearCache] Error:', err);
      const message = err instanceof Error ? err.message : 'Failed to clear cache';
      setError(message);
      throw err;
    } finally {
      setIsClearing(false);
    }
  }, []);

  return {
    clear,
    isClearing,
    error,
  };
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to find a single institution by ID from the cached list
 */
export function useInstitution(institutionId: string | null) {
  const { institutions, isLoading, error } = useRatedInstitutionsWithRatings();

  const institution = useMemo(() => {
    if (!institutionId) return null;
    return institutions.find((i) => i.institution_id === institutionId) || null;
  }, [institutions, institutionId]);

  return {
    institution,
    isLoading,
    error,
  };
}

/**
 * Hook to filter institutions by segment
 */
export function useInstitutionsBySegment(segmentId: string | null) {
  const { institutions, isLoading, error } = useRatedInstitutionsWithRatings();

  const filtered = useMemo(() => {
    if (!segmentId) return institutions;
    return institutions.filter((i) => i.segment_id === segmentId);
  }, [institutions, segmentId]);

  return {
    institutions: filtered,
    isLoading,
    error,
  };
}

/**
 * Hook to filter institutions by province
 */
export function useInstitutionsByProvince(province: string | null) {
  const { institutions, isLoading, error } = useRatedInstitutionsWithRatings();

  const filtered = useMemo(() => {
    if (!province) return institutions;
    return institutions.filter((i) => i.province === province);
  }, [institutions, province]);

  return {
    institutions: filtered,
    isLoading,
    error,
  };
}

export default {
  useRatedInstitutions,
  useRatedInstitutionsWithRatings,
  useLatestRating,
  useRatingHistory,
  useIndicatorScores,
  useCacheStats,
  useClearCache,
  useInstitution,
  useInstitutionsBySegment,
  useInstitutionsByProvince,
};
